import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../store/auth.store'
import { useTheme } from '../../store/theme.store'
import { useImpersonation } from '../../store/impersonation.store'
import { useMailboxes } from '../../hooks/useMailboxes'
import { LogOut, User, Bell, ChevronDown, Clock, Sun, Moon, Mail, X } from 'lucide-react'
import { useState, useRef, useCallback, useEffect } from 'react'
import { cn } from '../atoms'
import { useDashboardSummary } from '../../hooks/useDashboard'
import { useAlerts } from '../../hooks/useAlerts'
import { useUnseenNotifications } from '../../hooks/useUnseenNotifications'
import { getAssetUrl } from '../../utils/assetUrl'

function timeAgo(date: string) {
  const diff = Date.now() - new Date(date).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'ahora'
  if (mins < 60) return `hace ${mins} min`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `hace ${hrs}h`
  const days = Math.floor(hrs / 24)
  return `hace ${days}d`
}

function useDropdownPosition(buttonRef: React.RefObject<HTMLButtonElement | null>) {
  const [pos, setPos] = useState({ top: 0, right: 0 })
  const update = useCallback(() => {
    if (buttonRef.current) {
      const r = buttonRef.current.getBoundingClientRect()
      setPos({ top: r.bottom + 4, right: window.innerWidth - r.right })
    }
  }, [buttonRef])
  return { pos, update }
}

export function Header() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { user, logout } = useAuth()
  const { effectiveTheme, toggle } = useTheme()
  const { impersonateMailboxId, impersonateMailboxName, setImpersonateMailbox, clear } = useImpersonation()
  const { data: mailboxes } = useMailboxes()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const [showMailboxPicker, setShowMailboxPicker] = useState(false)
  const { data: dashboard } = useDashboardSummary(impersonateMailboxId)
  const { data: alertsData } = useAlerts({ limit: 5 })
  const { unseenAlertsCount, markAlertsAsSeen } = useUnseenNotifications()
  const alertCount = unseenAlertsCount
  const recentAlerts = (alertsData?.data || []).filter((a: any) => a.status !== 'RESOLVED').slice(0, 5)
  const isAdmin = user?.role === 'ADMIN'

  const bellRef = useRef<HTMLButtonElement>(null)
  const userRef = useRef<HTMLButtonElement>(null)
  const mailboxRef = useRef<HTMLButtonElement>(null)
  const notif = useDropdownPosition(bellRef)
  const userDd = useDropdownPosition(userRef)
  const mailboxDd = useDropdownPosition(mailboxRef)

  const invalidateDomainQueries = useCallback(() => {
    const domainKeys = [
      'emails',
      'cases',
      'alerts',
      'dashboard',
      'email-stats',
      'case-stats',
      'alertStats',
      'unified-labels',
      'email-groups',
      'gmail-labels',
    ]
    domainKeys.forEach((key) => {
      qc.invalidateQueries({ queryKey: [key] })
    })
  }, [qc])

  const handleSelectMailbox = (id: string | null, name: string | null) => {
    setImpersonateMailbox(id, name)
    invalidateDomainQueries()
  }

  useEffect(() => {
    if (showNotifications) notif.update()
    if (showUserMenu) userDd.update()
    if (showMailboxPicker) mailboxDd.update()
  }, [showNotifications, showUserMenu, showMailboxPicker, notif, userDd, mailboxDd])

  return (
    <header className="h-14 shrink-0 flex items-center justify-between px-4 md:px-6 border-b border-gray-200 bg-white dark:bg-gray-900 dark:border-gray-700">
      <div className="flex items-center gap-3">
        <h1 className="text-lg font-semibold text-gray-900 md:hidden">ComCenter</h1>
      </div>

      <div className="flex items-center gap-1">
        <button
          onClick={toggle}
          className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          title={effectiveTheme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
          aria-label="Cambiar tema"
        >
          {effectiveTheme === 'dark' ? (
            <Sun className="w-[18px] h-[18px]" />
          ) : (
            <Moon className="w-[18px] h-[18px]" />
          )}
        </button>

        <button
          ref={bellRef}
          onClick={() => { 
            setShowNotifications(!showNotifications)
            setShowUserMenu(false)
            markAlertsAsSeen()
          }}
          className="relative p-2 text-gray-500 hover:text-gray-700 transition-colors rounded-lg hover:bg-gray-100"
          title="Notificaciones"
        >
          <Bell className="w-[18px] h-[18px]" />
          {alertCount > 0 && (
            <span className="absolute top-1 right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[9px] font-bold text-white bg-red-500 rounded-full leading-none">
              {alertCount}
            </span>
          )}
        </button>

        <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-1" />

        {isAdmin && (
          <>
            <button
              ref={mailboxRef}
              onClick={() => { setShowMailboxPicker(!showMailboxPicker); setShowNotifications(false); setShowUserMenu(false) }}
              className={cn(
                'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[13px] font-medium transition-colors',
                impersonateMailboxId
                  ? 'bg-primary-50 text-primary-700 hover:bg-primary-100 dark:bg-primary-900/30 dark:text-primary-300'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'
              )}
            >
              <Mail className="w-4 h-4" />
              <span className="hidden lg:inline">{impersonateMailboxName || 'Todos los correos'}</span>
              {impersonateMailboxId && (
                <X
                  className="w-3 h-3 ml-0.5 hover:text-red-500 cursor-pointer"
                  onClick={(e) => { e.stopPropagation(); handleSelectMailbox(null, null) }}
                />
              )}
              <ChevronDown className="w-3 h-3" />
            </button>

            {showMailboxPicker && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowMailboxPicker(false)} />
                <div
                  className="fixed z-50 w-72 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                  style={{ top: mailboxDd.pos.top, right: mailboxDd.pos.right }}
                >
                  <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Filtrar por correo</h3>
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">Ver solo casos, emails y alertas de un correo</p>
                  </div>
                  <div className="max-h-72 overflow-y-auto py-1">
                    <button
                      onClick={() => { handleSelectMailbox(null, null); setShowMailboxPicker(false) }}
                      className={cn(
                        'flex items-center gap-2.5 w-full px-4 py-2.5 text-left text-[13px] transition-colors',
                        !impersonateMailboxId
                          ? 'bg-primary-50 text-primary-700 font-medium dark:bg-primary-900/30 dark:text-primary-300'
                          : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800/60'
                      )}
                    >
                      <div className="w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600" />
                      Todos los correos
                    </button>
                    {mailboxes?.map((mailbox) => (
                      <button
                        key={mailbox.id}
                        onClick={() => { handleSelectMailbox(mailbox.id, mailbox.name); setShowMailboxPicker(false) }}
                        className={cn(
                          'flex items-center gap-2.5 w-full px-4 py-2.5 text-left text-[13px] transition-colors',
                          impersonateMailboxId === mailbox.id
                            ? 'bg-primary-50 text-primary-700 font-medium dark:bg-primary-900/30 dark:text-primary-300'
                            : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800/60'
                        )}
                      >
                        <div className="w-2 h-2 rounded-full bg-primary-500" />
                        <div className="flex flex-col">
                          <span>{mailbox.name}</span>
                          {mailbox.email && <span className="text-[11px] text-gray-400">{mailbox.email}</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}

        <button
          ref={userRef}
          onClick={() => { setShowUserMenu(!showUserMenu); setShowNotifications(false) }}
          className="flex items-center gap-2.5 pl-2 pr-1 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          {user?.avatarUrl ? (
            <img
              src={getAssetUrl(user.avatarUrl)}
              alt="Avatar"
              className="w-8 h-8 rounded-full object-cover ring-2 ring-gray-100 dark:ring-gray-700"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
            />
          ) : (
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-600 text-white text-xs font-bold">
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
          )}
          <div className="hidden sm:block text-left">
            <p className="text-[13px] font-medium text-gray-800 dark:text-gray-100 leading-tight">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="text-[11px] text-gray-400 dark:text-gray-500 leading-tight uppercase tracking-wide">{user?.role}</p>
          </div>
          <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
        </button>
      </div>

      {showNotifications && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)} />
          <div
            className="fixed z-50 w-96 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
            style={{ top: notif.pos.top, right: notif.pos.right }}
          >
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/30">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Notificaciones</h3>
                {alertCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
                    {alertCount}
                  </span>
                )}
              </div>
              <span className="text-xs text-gray-400 dark:text-gray-500">sin resolver</span>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {recentAlerts.length === 0 ? (
                <div className="px-4 py-10 text-center">
                  <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-3">
                    <Bell className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Sin alertas activas</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Todas las alertas han sido resueltas</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50 dark:divide-gray-800">
                  {recentAlerts.map((alert: any) => {
                    const sev = alert.severity?.name || alert.severity || 'low'
                    const sevConfig: Record<string, { dot: string; badge: string }> = {
                      critical: { dot: 'bg-red-500', badge: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' },
                      high: { dot: 'bg-amber-500', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300' },
                      medium: { dot: 'bg-blue-500', badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' },
                      low: { dot: 'bg-gray-400', badge: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
                    }
                    const cfg = sevConfig[sev] || sevConfig.low
                    return (
                      <button
                        key={alert.id}
                        onClick={() => { setShowNotifications(false); navigate(`/alerts/${alert.id}`) }}
                        className="flex items-start gap-3 w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
                      >
                        <div className={cn('mt-0.5 w-2 h-2 rounded-full shrink-0', cfg.dot)} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-[13px] font-medium text-gray-900 dark:text-gray-100 truncate">{alert.title}</p>
                            <span className={cn('px-1.5 py-px rounded text-[10px] font-semibold shrink-0 uppercase tracking-wider', cfg.badge)}>
                              {sev}
                            </span>
                          </div>
                          {alert.description && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{alert.description}</p>
                          )}
                          <p className="flex items-center gap-1 text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                            <Clock className="w-3 h-3" />
                            {timeAgo(alert.createdAt)}
                          </p>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
            {recentAlerts.length > 0 && (
              <div className="px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30">
                <button
                  onClick={() => { setShowNotifications(false); navigate('/alerts') }}
                  className="block w-full text-center text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 py-1 transition-colors"
                >
                  Ver todas las alertas
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {showUserMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
          <div
            className="fixed z-50 w-56 bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-1"
            style={{ top: userDd.pos.top, right: userDd.pos.right }}
          >
            <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
              {user?.avatarUrl ? (
                <img
                  src={getAssetUrl(user.avatarUrl)}
                  alt="Avatar"
                  className="w-10 h-10 rounded-full object-cover ring-2 ring-gray-100 dark:ring-gray-700 shrink-0"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                />
              ) : (
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-600 text-white text-sm font-bold shrink-0">
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{user?.email}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Cerrar sesión
            </button>
          </div>
        </>
      )}
    </header>
  )
}
